import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { FaSearch, FaUserCircle, FaTimes } from 'react-icons/fa';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import { getAutoReplies, saveAutoReply } from '../../../Services/User/Chat';
import { getChatList } from '../../../Services/User/Chat';
import { showToast } from '../../../Components/Toast/toast';
import CustomButton from '../../../Components/CustomButton';
import heartDecor from '../../../assets/images/coin-store-figma/decor-heart.svg';
import './AutoReply.css';

const defaultAutoReplies = [
  'Hi! Thanks for accepting my request.',
  'Hello ! i am offline talk to you later',
  'Hello ! lets talk laater i am busy right now',
];

const AutoReply = () => {
  usePageTitle('Auto Reply');
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [chatList, setChatList] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [autoReplies, setAutoReplies] = useState([]);
  const [selectedAutoReply, setSelectedAutoReply] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const imageBaseUrl = import.meta.env.VITE_REACT_APP_IMAGE_URL || '';

  const getSafeText = (value, fallback = '') => {
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (value && typeof value === 'object') {
      if (typeof value.message === 'string' || typeof value.message === 'number') return String(value.message);
      if (typeof value.text === 'string' || typeof value.text === 'number') return String(value.text);
      if (typeof value.reply === 'string' || typeof value.reply === 'number') return String(value.reply);
    }
    return fallback;
  };

  const normalizeReplyItem = (item, index) => {
    const text = getSafeText(item, '').trim();
    if (!text) return null;
    const id = item && typeof item === 'object' && item.id != null ? item.id : `auto-reply-${index}`;
    return { id, text };
  };

  const resolveImageSrc = (pathOrUrl) => {
    if (!pathOrUrl || typeof pathOrUrl !== 'string') return '';
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    if (!imageBaseUrl) return pathOrUrl;
    const base = imageBaseUrl.endsWith('/') ? imageBaseUrl.slice(0, -1) : imageBaseUrl;
    const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${base}${path}`;
  };

  useEffect(() => {
    fetchAutoReplies();
    fetchChatList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchChatList(), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchChatList = async () => {
    try {
      setLoading(true);
      const response = await getChatList(searchQuery);
      const raw = response?.data ?? response;
      const chatListData = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      setChatList(chatListData);
    } catch (error) {
      console.error('Error fetching chat list:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutoReplies = async () => {
    try {
      const response = await getAutoReplies();
      const rawReplies = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

      const normalized = rawReplies
        .map((item, index) => normalizeReplyItem(item, index))
        .filter(Boolean);

      setAutoReplies(
        normalized.length > 0
          ? normalized
          : defaultAutoReplies.map((text, index) => ({ id: `default-${index}`, text }))
      );
    } catch (error) {
      console.error('Error fetching auto replies:', error);
      setAutoReplies(defaultAutoReplies.map((text, index) => ({ id: `default-${index}`, text })));
    }
  };

  const handleSave = async () => {
    if (!selectedChat) {
      showToast('Please select a user', 'error');
      return;
    }

    const messageToSave = customMessage.trim() || selectedAutoReply;
    if (!messageToSave) {
      showToast('Please enter a message or select an auto reply', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await saveAutoReply(messageToSave);
      if (response?.status) {
        setShowSuccessModal(true);
      } else {
        showToast(getSafeText(response?.message, 'Failed to save auto reply'), 'error');
      }
    } catch (error) {
      console.error('Error saving auto reply:', error);
      showToast(getSafeText(error?.message, 'Failed to save auto reply'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auto-reply-page">
      <img src={heartDecor} alt="" className="auto-reply-heart" aria-hidden="true" />
      <div className="auto-reply-hero" />

      <Container className="auto-reply-wrap">
        <div className="auto-reply-head">
          <button className="auto-reply-back" onClick={() => navigate('/chat')} aria-label="Go back">
            <FaArrowLeftLong />
          </button>
          <h1>Auto Reply</h1>
        </div>

        <div className="auto-reply-layout">
          <aside className="auto-reply-left">
            <div className="chat-search auto-reply-search">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <FaSearch className="search-icon" />
            </div>

            <div className="auto-reply-user-list">
              {loading && chatList.length === 0 ? (
                <div className="text-center py-4">Loading...</div>
              ) : chatList.length === 0 ? (
                <div className="text-center py-4">No users found</div>
              ) : (
                chatList.map((chat) => (
                  <div
                    key={chat.id}
                    className={`auto-reply-user-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedChat(chat);
                      setSelectedAutoReply('');
                      setCustomMessage('');
                    }}
                  >
                    <div className="auto-reply-user-avatar">
                      {chat.image ? (
                        <img src={resolveImageSrc(chat.image)} alt={chat.name || 'User'} />
                      ) : (
                        <FaUserCircle className="avatar-icon" />
                      )}
                    </div>
                    <div className="auto-reply-user-text">
                      <h6>{chat.name || `Chat #${chat.id}`}</h6>
                      <p>lorem ipsum sit amet</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </aside>

          <main className="auto-reply-right">
            <h2>Create Your Own Reply:</h2>
            <textarea
              placeholder="Write here"
              value={customMessage}
              onChange={(e) => {
                setCustomMessage(e.target.value);
                setSelectedAutoReply('');
              }}
              rows={4}
            />

            <h2>Choose Auto Reply:</h2>
            <div className="auto-reply-options">
              {autoReplies.map((reply, index) => (
                <label key={reply.id || index} className="auto-reply-option">
                  <input
                    type="radio"
                    name="autoReply"
                    value={reply.text}
                    checked={selectedAutoReply === reply.text}
                    onChange={() => {
                      setSelectedAutoReply(reply.text);
                      setCustomMessage('');
                    }}
                  />
                  <span>{reply.text}</span>
                </label>
              ))}
            </div>

            <div className="auto-reply-actions">
              <CustomButton
                className="auto-reply-save-btn"
                onClick={handleSave}
                disabled={loading || !selectedChat}
              >
                {loading ? 'Saving...' : 'Save'}
              </CustomButton>
            </div>
          </main>
        </div>
      </Container>

      {showSuccessModal && (
        <div className="auto-reply-success-overlay">
          <div className="auto-reply-success-modal">
            <button
              className="auto-reply-success-close"
              onClick={() => {
                setShowSuccessModal(false);
                navigate('/chat');
              }}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <div className="auto-reply-success-check">✓</div>
            <h3>Successful</h3>
            <p>Auto reply saved successfully!</p>

            <button
              className="auto-reply-success-ok"
              onClick={() => {
                setShowSuccessModal(false);
                navigate('/chat');
              }}
            >
              Ok
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default AutoReply;
